import { cn } from "lib/utils";
import { useState } from "react";
import { lpConfig } from "routes/LandingPage/lpConfig";
import { useFadeIn } from "routes/LandingPage/useFadeIn";

/**
 * 数値を日本円のカンマ区切りフォーマットに変換する
 * @param {number} value - フォーマット対象の数値
 * @return {string} カンマ区切りの文字列
 */
function formatYen(value: number): string {
  return `\u00A5${Math.round(value).toLocaleString()}`;
}

/**
 * 時間数をフォーマットする
 * @param {number} hours - 時間数
 * @return {string} フォーマットされた時間文字列
 */
function formatHours(hours: number): string {
  return `${Math.round(hours).toLocaleString()}時間`;
}

/**
 * インタラクティブなROI計算セクション
 * スライダーで営業担当者数・処理時間・時給・マージンを入力し、
 * コスト削減効果と粗利増加効果をリアルタイムで算出する
 * @return {JSX.Element} ROI計算セクションコンポーネント
 */
export function LpRoiCalculator() {
  const { ref, isVisible } = useFadeIn();
  const [staffCount, setStaffCount] = useState(
    lpConfig.roiCalculator.defaults.staffCount,
  );
  const [dailyHours, setDailyHours] = useState(
    lpConfig.roiCalculator.defaults.dailyHours,
  );
  const [hourlyRate, setHourlyRate] = useState(
    lpConfig.roiCalculator.defaults.hourlyRate,
  );
  const [marginPerDeal, setMarginPerDeal] = useState(
    lpConfig.roiCalculator.defaults.marginPerDeal,
  );

  // コスト削減
  const monthlyEmailCost =
    staffCount *
    dailyHours *
    lpConfig.roiCalculator.workingDaysPerMonth *
    hourlyRate;
  const monthlySavedHours =
    staffCount *
    dailyHours *
    lpConfig.roiCalculator.reductionRate *
    lpConfig.roiCalculator.workingDaysPerMonth;
  const monthlySavings = monthlySavedHours * hourlyRate;
  const yearlySavings = monthlySavings * 12;

  // 粗利増加
  // 現状: 1人あたり月の稼働時間（8h × 20日 = 160h）のうち、メール処理に dailyHours × 20 を消費
  // → 残り時間で1件成約と仮定
  // 導入後: メール処理時間が99%削減 → 空いた時間で追加案件を処理可能
  const monthlyWorkHours =
    lpConfig.roiCalculator.workingHoursPerDay *
    lpConfig.roiCalculator.workingDaysPerMonth; // 160h/人/月
  const currentEmailHours =
    dailyHours * lpConfig.roiCalculator.workingDaysPerMonth; // 現状のメール処理時間/人/月
  const coreHoursBefore = monthlyWorkHours - currentEmailHours; // 現状のコア業務時間
  const coreHoursAfter =
    monthlyWorkHours -
    currentEmailHours * (1 - lpConfig.roiCalculator.reductionRate); // 導入後のコア業務時間
  const dealMultiplier =
    coreHoursBefore > 0 ? coreHoursAfter / coreHoursBefore : 1;
  const currentDealsPerPerson = lpConfig.roiCalculator.currentDealsPerPerson; // 現状: 1人1件/月
  const newDealsPerPerson = currentDealsPerPerson * dealMultiplier;
  const additionalDeals =
    (newDealsPerPerson - currentDealsPerPerson) * staffCount;
  const monthlyProfitIncrease = additionalDeals * marginPerDeal;
  const yearlyProfitIncrease = monthlyProfitIncrease * 12;

  // 総合効果
  const yearlyTotalEffect = yearlySavings + yearlyProfitIncrease;

  return (
    <section className="py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
      <div
        ref={ref}
        className={cn(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          isVisible && "lp-visible",
        )}
      >
        <div className="text-center">
          <p className="text-xs font-bold tracking-widest text-[#1667CF] uppercase mb-4">
            {lpConfig.roiCalculator.sectionLabel}
          </p>
          <h2 className="text-4xl font-bold text-[#0F1649] mb-4">
            {lpConfig.roiCalculator.heading}
          </h2>
          <p className="text-gray-500 mb-12">
            {lpConfig.roiCalculator.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Input Sliders */}
          <div className="space-y-8">
            {/* Staff Count */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {lpConfig.roiCalculator.sliders.staffCount.label}
                </label>
                <span className="text-[#ff8d00] font-bold text-2xl">
                  {staffCount}
                  {lpConfig.roiCalculator.sliders.staffCount.unit}
                </span>
              </div>
              <input
                type="range"
                min={lpConfig.roiCalculator.sliders.staffCount.min}
                max={lpConfig.roiCalculator.sliders.staffCount.max}
                step={lpConfig.roiCalculator.sliders.staffCount.step}
                value={staffCount}
                onChange={e => setStaffCount(Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Daily Hours */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {lpConfig.roiCalculator.sliders.dailyHours.label}
                </label>
                <span className="text-[#ff8d00] font-bold text-2xl">
                  {dailyHours}
                  {lpConfig.roiCalculator.sliders.dailyHours.unit}
                </span>
              </div>
              <input
                type="range"
                min={lpConfig.roiCalculator.sliders.dailyHours.min}
                max={lpConfig.roiCalculator.sliders.dailyHours.max}
                step={lpConfig.roiCalculator.sliders.dailyHours.step}
                value={dailyHours}
                onChange={e => setDailyHours(Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Hourly Rate */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {lpConfig.roiCalculator.sliders.hourlyRate.label}
                </label>
                <span className="text-[#ff8d00] font-bold text-2xl">
                  {hourlyRate.toLocaleString()}
                  {lpConfig.roiCalculator.sliders.hourlyRate.unit}
                </span>
              </div>
              <input
                type="range"
                min={lpConfig.roiCalculator.sliders.hourlyRate.min}
                max={lpConfig.roiCalculator.sliders.hourlyRate.max}
                step={lpConfig.roiCalculator.sliders.hourlyRate.step}
                value={hourlyRate}
                onChange={e => setHourlyRate(Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Margin per Deal */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {lpConfig.roiCalculator.sliders.marginPerDeal.label}
                </label>
                <span className="text-[#ff8d00] font-bold text-2xl">
                  {marginPerDeal.toLocaleString()}
                  {lpConfig.roiCalculator.sliders.marginPerDeal.unit}
                </span>
              </div>
              <input
                type="range"
                min={lpConfig.roiCalculator.sliders.marginPerDeal.min}
                max={lpConfig.roiCalculator.sliders.marginPerDeal.max}
                step={lpConfig.roiCalculator.sliders.marginPerDeal.step}
                value={marginPerDeal}
                onChange={e => setMarginPerDeal(Number(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          {/* Right: Results Card */}
          <div className="space-y-6">
            {/* Loss Aversion Callout */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <p className="text-sm text-red-700 font-bold mb-1">
                {lpConfig.roiCalculator.labels.lossAversionPrefix}
                {formatYen(monthlyEmailCost)}
              </p>
              <p className="text-xs text-red-600">
                {lpConfig.roiCalculator.labels.lossAversionSuffix}
              </p>
            </div>

            {/* Cost Reduction */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-[#0F1649] mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#1667CF]" />
                {lpConfig.roiCalculator.labels.costReduction}
              </h3>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 text-sm">
                  {lpConfig.roiCalculator.labels.monthlySavedHours}
                </span>
                <span className="text-xl font-bold text-[#02072F]">
                  {formatHours(monthlySavedHours)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-gray-600 text-sm">
                  {lpConfig.roiCalculator.labels.yearlyCostReduction}
                </span>
                <span className="text-xl font-bold text-[#02072F]">
                  {formatYen(yearlySavings)}
                </span>
              </div>
            </div>

            {/* Profit Increase */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-[#0F1649] mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#30BF78]" />
                {lpConfig.roiCalculator.labels.profitIncrease}
              </h3>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 text-sm">
                  {lpConfig.roiCalculator.labels.dealsPerPerson}
                </span>
                <span className="text-xl font-bold text-[#02072F]">
                  月{currentDealsPerPerson}件 → {newDealsPerPerson.toFixed(1)}件
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600 text-sm">
                  {lpConfig.roiCalculator.labels.additionalDeals}
                </span>
                <span className="text-xl font-bold text-[#02072F]">
                  月+{additionalDeals.toFixed(1)}件
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-gray-600 text-sm">
                  {lpConfig.roiCalculator.labels.yearlyProfitIncrease}
                </span>
                <span className="text-xl font-bold text-[#30BF78]">
                  {formatYen(yearlyProfitIncrease)}
                </span>
              </div>
            </div>

            {/* Total */}
            <div className="bg-[#02072F] rounded-2xl p-6 text-center">
              <p className="text-gray-400 text-sm mb-1">
                {lpConfig.roiCalculator.labels.yearlyTotal}
              </p>
              <p className="text-3xl sm:text-4xl font-bold text-[#ff8d00]">
                {formatYen(yearlyTotalEffect)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
